import { styled, Theme, SxProps, Box, Breakpoint } from '@mui/material';
import { PropsWithChildren } from 'react';

type Props = {
  justify?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
  centered?: boolean;
  breakpoint?: Breakpoint;
  spacing?: keyof Theme['spacingSizes'];
  className?: string;
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
};

const shouldForwardProp = (prop: string) =>
  !['fullWidth', 'centered', 'spacing', 'justify', 'breakpoint'].includes(prop);

type StyledRowProps = Pick<
  Props,
  'centered' | 'breakpoint' | 'spacing' | 'justify' | 'fullWidth'
>;

const StyledRow = styled(Box, {
  shouldForwardProp,
})<StyledRowProps>(
  ({
    theme,
    centered,
    breakpoint,
    spacing,
    justify = 'flex-start',
    fullWidth,
  }) => ({
    display: 'flex',
    justifyContent: justify,
    alignItems: centered ? 'center' : undefined,
    gap: spacing ? theme.spacing(theme.spacingSizes[spacing]) : 0,
    flexDirection: 'row',
    width: fullWidth ? '100%' : undefined,

    [theme.breakpoints.down(breakpoint ?? 'xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',

      '& > *': {
        width: '100%',
      },
    },
  }),
);

const Row: React.FC<PropsWithChildren<Props>> = ({
  children,
  spacing,
  className,
  fullWidth,
  onClick,
  ...props
}) => {
  return (
    <StyledRow
      className={className}
      spacing={spacing}
      fullWidth={fullWidth}
      onClick={onClick}
      {...props}
    >
      {children}
    </StyledRow>
  );
};

export default Row;
